import { yupResolver } from '@hookform/resolvers/yup';
import { type SchemaOption, toSchemaOptions } from '@myosh/logic';
import { Button, Field, FieldError, HfSwitch, SchemaCombobox } from '@myosh/odin-components';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import i18next from 'i18next';
import { useEffect, useId, useMemo, useState } from 'react';
import { Controller, type FieldValues, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useAuth } from '../context/auth.context';
import { useUser } from '../context/user.context';
import { UserActionTypes } from '../context/user.state';
import { decodeIdToken } from '../helpers/decode-id-token';
import { schemasQueryOptions } from '../helpers/get-schemas';
import { ControlledUserPicker } from './controlled-user-picker.component';
import Footer from './footer.component';
import Sidebar from './sidebar.component';

const superUserFormSchema = yup.object().shape({
  schema: yup.object().required(() => i18next.t(($) => $.schemaRequired)),
});

const standardFormSchema = yup.object().shape({
  schema: yup.object().required(() => i18next.t(($) => $.companyRequired)),
});

export interface LoginSchemaProps {
  idToken: string;
  onSchemaSelected: (schema: string) => void;
}

function SchemaRedirect({ schema, onSchemaSelected }: { schema: string; onSchemaSelected: (schema: string) => void }) {
  const { dispatch } = useUser();
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only redirect; the schema is fixed by this point
  useEffect(() => {
    setTimeout(() => onSchemaSelected(schema)); // allows state to persist before redirecting (MYOSH-8225)
    dispatch({ type: UserActionTypes.UpdateCurrentSchema, payload: schema });
    dispatch({ type: UserActionTypes.UpdateLoginStatus, payload: 'done' });
  }, []);
  return null;
}

interface SchemaPickerFormProps {
  idToken: string;
  isSuperUser: boolean;
  schemas: SchemaOption[];
  isLoading?: boolean;
  onSchemaSelected: (schema: string) => void;
}

function SchemaPickerForm({ idToken, isSuperUser, schemas, isLoading, onSchemaSelected }: SchemaPickerFormProps) {
  const { t } = useTranslation();
  const schemaErrorId = useId();
  const { dispatch } = useUser();
  const { control, handleSubmit, clearErrors } = useForm<FieldValues>({
    resolver: yupResolver(isSuperUser ? superUserFormSchema : standardFormSchema) as never,
    defaultValues: { schema: null, toggle: false },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const controlUserEnabled = !!useWatch({ control, name: 'toggle' });
  const selectedSchema = useWatch({ control, name: 'schema' }) as SchemaOption | undefined;
  const [controlledUser, setControlledUser] = useState<string | null>(null);
  const [userError, setUserError] = useState<string>();

  const onSubmit = (data: FieldValues) => {
    if (isSuperUser && controlUserEnabled) {
      if (!controlledUser) {
        setUserError(t(($) => $.controlUserRequired));
        return;
      }
      dispatch({ type: UserActionTypes.UpdateControlledUsername, payload: controlledUser });
    }
    setUserError(undefined);

    dispatch({ type: UserActionTypes.UpdateCurrentSchema, payload: data.schema.value });
    setTimeout(() => onSchemaSelected(data.schema.value)); // MYOSH-8225
    dispatch({ type: UserActionTypes.UpdateLoginStatus, payload: 'done' });
  };

  return (
    <form data-testid="myl-select-schema-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Controller
        control={control}
        name="schema"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <SchemaCombobox
              id={field.name}
              schemas={schemas}
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                setControlledUser(null);
              }}
              placeholder={isSuperUser ? t(($) => $.selectSchema) : t(($) => $.selectCompany)}
              isLoading={isLoading}
              aria-invalid={!!fieldState.error}
              aria-describedby={fieldState.error ? schemaErrorId : undefined}
              autoFocus
            />
            <FieldError id={schemaErrorId}>{fieldState.error?.message}</FieldError>
          </Field>
        )}
      />
      {isSuperUser && (
        <div className="flex flex-col gap-2">
          <HfSwitch
            control={control}
            name="toggle"
            label={t(($) => $.controlUser)}
            defaultValue={false}
            onChange={() => {
              clearErrors();
              setUserError(undefined);
              setControlledUser(null);
            }}
            disabled={!selectedSchema}
          />
          {controlUserEnabled && selectedSchema && (
            <ControlledUserPicker
              key={selectedSchema.value}
              idToken={idToken}
              schema={selectedSchema.value}
              value={controlledUser}
              onValueChange={(value) => {
                setControlledUser(value);
                if (value) {
                  setUserError(undefined);
                }
              }}
              error={userError}
            />
          )}
        </div>
      )}
      <Button type="submit" size="lg" className="self-start">
        {isSuperUser ? t(($) => $.selectSchema) : t(($) => $.selectCompany)}
      </Button>
    </form>
  );
}

function LoginSchemaContent({ idToken, onSchemaSelected }: LoginSchemaProps) {
  const { t } = useTranslation();
  const { dispatch, state } = useUser();
  const { logout } = useAuth();

  const { isSuperUser, shortcutSchema } = useMemo(() => decodeIdToken(idToken), [idToken]);

  const {
    data: fetchedSchemas,
    isPending: isLoadingSchemas,
    isError: schemasError,
  } = useQuery({
    ...schemasQueryOptions(idToken),
    enabled: Boolean(idToken && !shortcutSchema),
    select: (data) => toSchemaOptions(data.result ?? [], { isSuperUser }),
  });

  useEffect(() => {
    if (fetchedSchemas) {
      dispatch({ type: UserActionTypes.UpdateSchemas, payload: fetchedSchemas.map((s) => s.value).join(',') });
    }
  }, [fetchedSchemas, dispatch]);

  useEffect(() => {
    if (schemasError) {
      dispatch({ type: UserActionTypes.UpdateLoginStatus, payload: 'login' });
      logout();
    }
  }, [schemasError, dispatch, logout]);

  const autoRedirectSchema = shortcutSchema ?? (fetchedSchemas?.length === 1 ? fetchedSchemas[0].value : undefined);

  return (
    <div className="myl-login-layout flex h-full min-h-screen w-full flex-col sm:flex-row">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col bg-background">
        <div className="flex max-w-sm flex-1 flex-col justify-center px-8 pb-12 md:ml-24 md:px-0">
          <h2 className="mt-8 mb-6 font-bold text-4xl sm:mt-12">
            {t(($) => $.welcomeUser, { name: state.user?.preferred_username ?? '' })}
          </h2>
          <p className="mb-4 sm:text-xl md:text-2xl">
            {isSuperUser ? t(($) => $.selectSchemaToContinue) : t(($) => $.selectCompanyToContinue)}
          </p>
          {autoRedirectSchema ? (
            <SchemaRedirect schema={autoRedirectSchema} onSchemaSelected={onSchemaSelected} />
          ) : (
            <SchemaPickerForm
              idToken={idToken}
              isSuperUser={isSuperUser}
              schemas={fetchedSchemas ?? []}
              isLoading={isLoadingSchemas}
              onSchemaSelected={onSchemaSelected}
            />
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
}

const queryClient = new QueryClient();

export default function LoginSchema(props: LoginSchemaProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <LoginSchemaContent {...props} />
    </QueryClientProvider>
  );
}
